import { useCallback, useEffect, useRef, useState } from 'react';
import { buildFilePageEdits, MAX_EDIT_PAGES } from '../../pdf/shared/pdfPageEditEngine';

export const fileKeyOf = (file) => `${file.name}-${file.size}-${file.lastModified}`;

/**
 * Manage editable page lists for a set of PDF files, keyed by file.
 * Values are an array of page-edit objects, or the string `'oversize'`.
 *
 * @param {File[]} files
 * @param {() => void} [onEdit] called after a page edit (e.g. to clear stale results)
 * @param {number} [maxPages] page cap above which a file is marked `'oversize'`
 */
export function usePdfPageEdits(files, onEdit, maxPages = MAX_EDIT_PAGES) {
    const [pagesByKey, setPagesByKey] = useState({});
    const builtKeysRef = useRef(new Set());

    useEffect(() => {
        let cancelled = false;
        const currentKeys = new Set(files.map(fileKeyOf));

        for (const key of Array.from(builtKeysRef.current)) {
            if (!currentKeys.has(key)) builtKeysRef.current.delete(key);
        }
        setPagesByKey((prev) => {
            const next = {};
            let changed = false;
            for (const key of Object.keys(prev)) {
                if (currentKeys.has(key)) next[key] = prev[key];
                else changed = true;
            }
            return changed ? next : prev;
        });

        (async () => {
            for (const file of files) {
                const key = fileKeyOf(file);
                if (builtKeysRef.current.has(key)) continue;
                builtKeysRef.current.add(key);
                try {
                    const built = await buildFilePageEdits(file, maxPages);
                    if (cancelled) return;
                    setPagesByKey((prev) => ({ ...prev, [key]: built }));
                } catch {
                    // Leave unbuilt so the file simply runs as-is; allow a later retry.
                    builtKeysRef.current.delete(key);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [files, maxPages]);

    const updatePagesForKey = useCallback((key, updater) => {
        setPagesByKey((prev) => {
            const current = Array.isArray(prev[key]) ? prev[key] : [];
            const nextPages = typeof updater === 'function' ? updater(current) : updater;
            return { ...prev, [key]: nextPages };
        });
        onEdit?.();
    }, [onEdit]);

    const resetPageEdits = useCallback(() => {
        setPagesByKey({});
        builtKeysRef.current.clear();
    }, []);

    return { pagesByKey, updatePagesForKey, resetPageEdits };
}
